import { Zone } from "@fairfleet/geotab";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MAX_ZONE_PAGES, MAX_ZONES_LOADED, RESULTS_LIMIT_ZONE } from "@/constants/resultsLimits";
import { fetchAllSortedById } from "@/utilities/query/fetchAllSortedById";

/**
 * Every zone the user may see, deliberately without the page's group filter: a zone just saved under
 * another group would otherwise vanish from the list and look like a failed save.
 *
 * Each zone also goes into the single-zone cache (`["zone", session, id]`) that `useZonesByIds` reads.
 */
export function useAllZones() {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();

  const query = useQuery({
    queryKey: ["zones", activeSessionId, "all"],
    queryFn: async ({ signal }) => {
      const zones = await fetchAllSortedById<"Zone", Zone>(
        geotabClient,
        "Zone",
        {},
        { pageSize: RESULTS_LIMIT_ZONE, maxPages: MAX_ZONE_PAGES, signal },
      );
      for (const zone of zones) queryClient.setQueryData(["zone", activeSessionId, zone.id], zone);
      return zones;
    },
    // A retry restarts paging at page 1.
    retry: 1,
  });

  return Object.assign(query, { isTruncated: (query.data?.length ?? 0) >= MAX_ZONES_LOADED });
}
